import { Capacitor } from '@capacitor/core'
import { db } from '../../database/schema.js'
import { logger } from '../../utils/logger.js'
import type { OpenBetaAreaDetail } from './openbeta-types.js'
import { parseGradeNumeric, V_OFFSET } from './openbeta-grades.js'

const log = logger.create('openbeta-search')

const ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const AREA_INDEX_URL = `${ORIGIN}/data/openbeta/openbeta-area-index.json`
const CLIMB_INDEX_URL = `${ORIGIN}/data/openbeta/openbeta-climb-index.json`
const AREA_DETAIL_URL = `${ORIGIN}/data/openbeta/areas`

const CACHE_KEY_AREAS = 'openbeta-area-index'
const CACHE_KEY_CLIMBS = 'openbeta-climb-index'
const INDEX_TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

// ── Index types (on-disk compact format) ──

interface AreaEntry {
  u: string
  n: string
  c: [number, number] // [lat, lng]
  t: number
  p: string[]
  d?: { s?: number; r?: number; b?: number; a?: number; t?: number }
  l?: boolean
}

interface ClimbIndex {
  n: string[]
  g: string[]
  t: number[]
  au: string[]
}

// ── Derived search fields (computed at load time) ──

interface AreaSearchable extends AreaEntry {
  s: string // normalized name (lowercase, stripped prefixes)
  k: string // keywords (path tokens + normalized name)
}

// ── Public result types ──

export interface OpenBetaAreaResult {
  uuid: string
  name: string
  coordinates: [number, number]
  totalClimbs: number
  pathTokens: string[]
  disciplines?: { s?: number; r?: number; b?: number; a?: number; t?: number }
  leaf?: boolean
  score: number
}

export interface OpenBetaClimbResult {
  name: string
  grade: string
  type: number
  areaUuid: string
  areaName: string
  areaCoordinates: [number, number]
  areaPathTokens: string[]
  score: number
}

export type OpenBetaSearchResult =
  | ({ kind: 'area' } & OpenBetaAreaResult)
  | ({ kind: 'climb' } & OpenBetaClimbResult)

// ── Name normalization (matches build-time logic) ──

const LEADING_PREFIX_RE = /^(?:[*#]+\s*|[([a-zA-Z0-9.]*[)\]]\s+|\d+[.)\\-]\s+)/

function normalize(name: string): string {
  return name
    .replace(LEADING_PREFIX_RE, '')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .toLowerCase()
    .trim()
}

// ── Scoring ──

function textScore(
  query: string,
  tokens: string[],
  name: string,
  keywords: string,
  totalClimbs: number,
  isLeaf: boolean
): number {
  let score = 0
  if (name === query) {
    score = 100
  } else if (name.startsWith(query)) {
    score = 80
  } else {
    for (const token of tokens) {
      if (keywords.includes(token)) score += 10
    }
  }
  if (score === 0) return 0
  score += Math.log2(Math.max(totalClimbs, 1))
  if (isLeaf) score += 5
  return score
}

function distanceDecay(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dlat = (lat2 - lat1) * 111.32
  const dlng = (lng2 - lng1) * 111.32 * Math.cos((lat1 * Math.PI) / 180)
  const km = Math.sqrt(dlat * dlat + dlng * dlng)
  return 1 / (1 + km / 100)
}

export interface ClimbFilterOptions {
  typeMask: number
  ydsMin: number
  ydsMax: number
  vMin: number
  vMax: number
}

class OpenBetaSearchService {
  private areas: AreaSearchable[] | null = null
  private climbs: ClimbIndex | null = null
  private climbNamesNormalized: string[] | null = null
  private climbGradeNums: number[] | null = null
  private areaByUuid: Map<string, AreaSearchable> | null = null
  private loading: Promise<void> | null = null
  private initStarted = false

  init(): void {
    if (this.initStarted) return
    this.initStarted = true
    this.loading = this.loadIndexes()
    this.loading.catch(() => {})
  }

  get isLoaded(): boolean {
    return this.areas !== null && this.climbs !== null
  }

  private async loadIndexes(): Promise<void> {
    try {
      const [areaData, climbData] = await Promise.all([
        this.loadIndex<AreaEntry[]>(CACHE_KEY_AREAS, AREA_INDEX_URL),
        this.loadIndex<ClimbIndex>(CACHE_KEY_CLIMBS, CLIMB_INDEX_URL),
      ])

      const areas: AreaSearchable[] = areaData.map(a => {
        const s = normalize(a.n)
        const pathNorm = a.p.map(t => t.toLowerCase())
        return { ...a, s, k: [...pathNorm, s].join(' ') }
      })

      this.areas = areas
      this.areaByUuid = new Map(areas.map(a => [a.u, a]))

      this.climbs = climbData
      this.climbNamesNormalized = climbData.n.map(normalize)
      this.climbGradeNums = climbData.g.map(parseGradeNumeric)

      log.info(`Indexes loaded: ${areas.length} areas, ${climbData.n.length} climbs`)
    } catch (err) {
      log.warn('Failed to load OpenBeta indexes:', err)
      this.areas = []
      this.climbs = { n: [], g: [], t: [], au: [] }
      this.climbNamesNormalized = []
      this.areaByUuid = new Map()
    } finally {
      this.loading = null
    }
  }

  private async loadIndex<T>(cacheKey: string, url: string): Promise<T> {
    try {
      const cached = await db.climbingCache.get(cacheKey)
      if (cached) {
        const age = Date.now() - new Date(cached.fetchedAt).getTime()
        if (age < INDEX_TTL_MS) {
          return cached.data as T
        }
        // Stale — try network, fall back to cached
        try {
          return await this.fetchAndCache<T>(cacheKey, url)
        } catch {
          return cached.data as T
        }
      }
    } catch {
      // IndexedDB not available
    }
    return this.fetchAndCache<T>(cacheKey, url)
  }

  private async fetchAndCache<T>(cacheKey: string, url: string): Promise<T> {
    const resp = await fetch(url)
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    const data = (await resp.json()) as T
    try {
      await db.climbingCache.put({ key: cacheKey, data, fetchedAt: new Date().toISOString() })
    } catch {
      // IndexedDB write failed
    }
    return data
  }

  async ensureLoaded(): Promise<void> {
    if (this.areas) return
    if (this.loading) return this.loading
    this.init()
    if (this.loading) return this.loading
  }

  search(query: string, biasPoint?: [number, number], limit = 10): OpenBetaSearchResult[] {
    if (!this.areas || !this.climbs || !this.climbNamesNormalized || !this.areaByUuid) return []

    const lower = query.toLowerCase().trim()
    if (lower.length < 2) return []
    const tokens = lower.split(/\s+/)

    const results: OpenBetaSearchResult[] = []

    // Score areas
    for (const area of this.areas) {
      let s = textScore(lower, tokens, area.s, area.k, area.t, area.l === true)
      if (s <= 0) continue
      if (biasPoint) s *= distanceDecay(biasPoint[0], biasPoint[1], area.c[0], area.c[1])
      const areaResult: OpenBetaSearchResult = {
        kind: 'area',
        uuid: area.u,
        name: area.n,
        coordinates: area.c,
        totalClimbs: area.t,
        pathTokens: area.p,
        score: s,
      }
      if (area.d) (areaResult as OpenBetaAreaResult).disciplines = area.d
      if (area.l) (areaResult as OpenBetaAreaResult).leaf = area.l
      results.push(areaResult)
    }

    // Score climbs
    const climbNames = this.climbNamesNormalized
    const climbs = this.climbs
    for (let i = 0; i < climbNames.length; i++) {
      const name = climbNames[i]
      let s = 0
      if (name === lower) {
        s = 100
      } else if (name.startsWith(lower)) {
        s = 80
      } else {
        for (const token of tokens) {
          if (name.includes(token)) s += 10
        }
      }
      if (s <= 0) continue

      const areaUuid = climbs.au[i]
      const area = this.areaByUuid.get(areaUuid)
      if (!area) continue

      if (biasPoint) s *= distanceDecay(biasPoint[0], biasPoint[1], area.c[0], area.c[1])

      results.push({
        kind: 'climb',
        name: climbs.n[i],
        grade: climbs.g[i],
        type: climbs.t[i],
        areaUuid,
        areaName: area.n,
        areaCoordinates: area.c,
        areaPathTokens: area.p,
        score: s,
      })
    }

    results.sort((a, b) => b.score - a.score)
    return results.slice(0, limit)
  }

  queryWithin(bbox: [number, number, number, number]): AreaSearchable[] {
    if (!this.areas) return []
    const [west, south, east, north] = bbox
    return this.areas.filter(a => {
      const [lat, lng] = a.c
      return lat >= south && lat <= north && lng >= west && lng <= east
    })
  }

  queryCragsNear(lat: number, lng: number, radiusM = 2000): AreaSearchable[] {
    const latDelta = radiusM / 111320
    const lngDelta = radiusM / (111320 * Math.cos((lat * Math.PI) / 180))
    return this.queryWithin([lng - lngDelta, lat - latDelta, lng + lngDelta, lat + latDelta])
  }

  async getAreaDetail(uuid: string): Promise<OpenBetaAreaDetail | null> {
    const cacheKey = `area-detail:${uuid}`
    try {
      const cached = await db.climbingCache.get(cacheKey)
      if (cached) return cached.data as OpenBetaAreaDetail
    } catch {
      // IndexedDB not available
    }

    try {
      const resp = await fetch(`${AREA_DETAIL_URL}/${uuid}.json`)
      if (!resp.ok) return null
      const detail = (await resp.json()) as OpenBetaAreaDetail
      try {
        await db.climbingCache.put({
          key: cacheKey,
          data: detail,
          fetchedAt: new Date().toISOString(),
        })
      } catch {
        // IndexedDB write failed
      }
      return detail
    } catch {
      return null
    }
  }

  getArea(uuid: string): AreaSearchable | undefined {
    return this.areaByUuid?.get(uuid)
  }

  getMatchingAreaUuids(filter: ClimbFilterOptions): Set<string> | null {
    if (!this.climbs || !this.climbGradeNums) return null

    const { typeMask, ydsMin, ydsMax, vMin, vMax } = filter
    const { t, au } = this.climbs
    const grades = this.climbGradeNums
    const result = new Set<string>()

    for (let i = 0; i < t.length; i++) {
      if (typeMask && !(t[i] & typeMask)) continue

      const g = grades[i]
      if (g < 0) {
        result.add(au[i])
        continue
      }

      if (g >= V_OFFSET) {
        const v = g - V_OFFSET
        if (v < vMin || v > vMax) continue
      } else {
        if (g < ydsMin || g > ydsMax) continue
      }

      result.add(au[i])
    }

    return result
  }
}

export const openBetaSearchService = new OpenBetaSearchService()
